import { useGetFonts } from "@/hooks/useGetFonts";
import { axiosGet } from "@/services/axiosService";
import React, { useEffect, useState } from "react";
import {
  AffordableSvg,
  BalancedSvg,
  BasicSvg,
  CodingSvg,
  ConsistentSvg,
  ContextSvg,
  FastIcon,
  GeneralistSvg,
  LightweightSvg,
  MutltimodalSvg,
  OpenSourceSvg,
  QualitySvg,
  ReasonSvg,
  ReliableSvg,
  StructuredSvg,
  TechnicalSvg,
  ToolsSvg,
  UltraFastSvg,
} from "@/assets/AiBotAssets";

const botTone = [
  {
    tone: "Friendly",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/pd1LNRxpSHeB02nm8aEg___.svg",
  },
  {
    tone: "Professional",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/AZQa5wYoQamwLLnIU6q9___.svg",
  },
  {
    tone: "Informative",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/guaQjAQNORaAaXrFGAPa___.svg",
  },
  // {
  //   tone: "Urgent",
  //   emoji: "https://dlvkyia8i4zmz.cloudfront.net/c2KxnEmfRc2boafchSxh__.svg",
  // },
  // {
  //   tone: "Humorous",
  //   emoji: "https://dlvkyia8i4zmz.cloudfront.net/ooQLXQp5RVWFRPEzOoSl___.svg",
  // },
  {
    tone: "Apologetic",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/m2HtWHK1RkaZdW5OkQhz___.svg",
  },
  {
    tone: "Appreciative",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/gVdh1cfHR9643L1Iml8R___.svg",
  },
  {
    tone: "Assertive",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/aGbxSxTWXOfO6QDjgiAv___.svg",
  },
  {
    tone: "Authoritative",
    emoji: "https://dlvkyia8i4zmz.cloudfront.net/IvQqJeZT2XMQlzqdhTOg___.svg",
  },
  {
    tone: "Witty",
    emoji:
      "https://dlvkyia8i4zmz.cloudfront.net/mY7ejzDRMWxhYOAhiZYk_Witty.svg",
  },
  {
    tone: "Sarcastic",
    emoji:
      "https://dlvkyia8i4zmz.cloudfront.net/RUqX2aKVS7Or3Qiw5rdw_Sarcastic.svg",
  },
  {
    tone: "Optimistic",
    emoji:
      "https://dlvkyia8i4zmz.cloudfront.net/zjNDsl96SFOLEvqsaROS_Optimistic.svg",
  },
  {
    tone: "Empathetic",
    emoji:
      "https://dlvkyia8i4zmz.cloudfront.net/HaLesr4QTSsdYtHpXHrS_Empathetic_emoji.svg",
  },
  {
    tone: "Informal",
    emoji: "https://cdn.filestackcontent.com/rO3qrtCkRlyzM3FKrjFH",
  },
];

export const APP_SUMO_USAGE_LIMIT_BUILDER = 50;
export const NEW_USER_INSTRUCTION = `
### 1. Train Your Bot with Data
- Add website, YouTube, sitemap links, or upload files (MP4, PDF, CSV, etc.) to train your bot.
- Connect apps like Notion, Salesforce, and Google Drive for diverse data sources.

### 2. Design & Configure Your Bot
- Set up your bot’s identity, behavior, chat enhancements, nudges, and customize its role and AI model.
- Define how your bot interacts and appears to users.

### 3. Deploy & Integrate
- Easily deploy your bot on websites, WhatsApp, Messenger, Telegram, and Instagram.
- Integrate with live chat platforms (Intercom, Zendesk) and 5000+ apps via Zapier.

### 4. Monitor, Use the “Improve Your Bot” Section, and Analyze
- Track key metrics like resolution, sentiment, user demographics, and popular topics.
- Use the Improve Your Bot and Analyze sections to measure performance and continuously optimize your bot.

### 5. Watch Video Instructions
- Refer to the video tutorials [here](https://support.chatbotbuilder.net/) for step-by-step guidance on training, configuring, and managing your chatbot to ensure smooth setup and effective use.

<links-cbb>
<li>https://support.chatbotbuilder.net/</li>
</links-cbb>
`;

const language = [
  "English (US)",
  "English (UK)",
  "German",
  "Spanish",
  "French",
  "Italian",
  "Portuguese",
  "Polish",
  "Ukrainian",
  "Somali",
  "Afrikaans",
  "Azerbaijani",
  "Indonesian",
  "Malaysian Malay",
  "Malay",
  "Javanese",
  "Sundanese",
  "Bosnian",
  "Catalan",
  "Czech",
  "Chichewa",
  "Welsh",
  "Danish",
  "Estonian",
  "Esperanto",
  "Basque",
  "Irish",
  "Galician",
  "Croatian",
  "Xhosa",
  "Zulu",
  "Icelandic",
  "Swahili",
  "Haitian Creole",
  "Kurdish",
  "Latin",
  "Latvian",
  "Luxembourgish",
  "Lithuanian",
  "Hungarian",
  "Malagasy",
  "Maltese",
  "Maori",
  "Dutch",
  "Norwegian",
  "Uzbek",
  "Romanian",
  "Sesotho",
  "Albanian",
  "Slovak",
  "Slovenian",
  "Finnish",
  "Swedish",
  "Tagalog",
  "Tatar",
  "Turkish",
  "Vietnamese",
  "Yoruba",
  "Greek",
  "Belarusian",
  "Bulgarian",
  "Kyrgyz",
  "Kazakh",
  "Macedonian",
  "Mongolian",
  "Russian",
  "Serbian",
  "Tajik",
  "Georgian",
  "Armenian",
  "Yiddish",
  "Hebrew",
  "Uyghur",
  "Urdu",
  "Arabic",
  "Pashto",
  "Persian",
  "Nepali",
  "Marathi",
  "Hindi",
  "Bengali",
  "Punjabi",
  "Gujarati",
  "Oriya",
  "Tamil",
  "Telugu",
  "Kannada",
  "Malayalam",
  "Sinhala",
  "Thai",
  "Lao",
  "Burmese",
  "Khmer",
  "Korean",
  "Chinese",
  "Traditional Chinese",
  "Japanese",
];
// const models = [
//   "gpt-3.5-turbo",
//   "gpt-3.5-turbo-0301",
//   "gpt-3.5-turbo-0613",
//   "gpt-3.5-turbo-16k",
//   "gpt-3.5-turbo-16k-0613",
//   "gpt-4",
//   "gpt-4-0613",
//   "gpt-4-32k",
//   "gpt-4-32k-0613",
// ];

//  const models = [
//   "4-turbo",
//   "4o",
//   "4o-mini",
//   "3.5-turbo",
//   "3.5-sonnet",
//   "3-sonnet",
//   "3-haiku",
//   "3-opus",
//   "none of these"
// ];

const openAiModels = [
  {
    // a
    name: "gpt-5",
    displayName: "OpenAI GPT 5 (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Think", "Actions", "Quality", "Complex"],
  },
  // {
  //   // a
  //   name: "gpt-5.1-thinking",
  //   displayName: "OpenAI GPT 5.1 Thinking",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
  //   categories: ["Think", "Quality", "Actions", "Complex"],
  // },
  // {
  //   // a
  //   name: "gpt-5.1-instant",
  //   displayName: "OpenAI GPT 5.1 Instant",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
  //   categories: ["Fast", "Actions", "Affordable", "Quality"],
  // },
  {
    // b
    name: "gpt-5-mini",
    displayName: "OpenAI GPT 5 Mini",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Actions", "Affordable", "Fast"],
  },
  // {
  //   // b
  //   name: "gpt-5-nano",
  //   displayName: "OpenAI GPT 5 Nano",
  //   url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
  //   categories: ["Fast", "Reliable", "Actions", "Think"],
  // },
  {
    //a
    name: "4.1",
    displayName: "OpenAI GPT 4.1",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Quality", "Actions", "Generalist"],
  },
  {
    //a
    name: "4o",
    displayName: "OpenAI GPT 4o",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Generalist", "Reliable", "Actions"],
  },
  {
    //b
    name: "4.1-nano",
    displayName: "OpenAI GPT 4.1 Nano",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Lightweight", "Basic", "Fast"],
  },
  {
    //b
    name: "4o-mini",
    displayName: "OpenAI GPT 4o Mini",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Fast", "Generalist", "Lightweight"],
  },
  {
    //a
    name: "4.1-mini",
    displayName: "OpenAI GPT 4.1 mini",
    url: "https://dlvkyia8i4zmz.cloudfront.net/2IiQm1X2SCeVRzqCiTYj_Frame__3_.svg",
    categories: ["Fast", "Generalist"],
  },
];

const claudeModels = [
  {
    name: "4.5-sonnet",
    displayName: "Anthropic Claude 4.5 Sonnet",
    url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
    categories: ["Quality", "Fast", "Actions", "Reliable"],
  },
  {
    name: "4.5-haiku",
    displayName: "Anthropic Claude 4.5 Haiku",
    url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
    categories: ["Quality", "Fast", "Actions", "Reliable", "Affordable"],
  },
  {
    name: "4-sonnet",
    displayName: "Anthropic Claude 4 Sonnet (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
    categories: ["Quality", "Fast", "Actions"],
  },
  {
    name: "3.7-sonnet",
    displayName: "Anthropic Claude 3.7 Sonnet",
    url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
    categories: ["Actions", "Quality"],
  },
  {
    name: "3.5-sonnet",
    displayName: "Anthropic Claude 3.5 Sonnet",
    url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
    categories: ["Actions", "Quality"],
  },
  {
    name: "3.5-haiku",
    displayName: "Anthropic Claude 3.5 Haiku",
    url: "https://dlvkyia8i4zmz.cloudfront.net/tKy3QadDRtu6XXBhp1Eq_Frame_1618874430.svg",
    categories: ["Actions", "Quality", "Fast"],
  },
];

const geminiModels = [
  {
    //a
    name: "Gemini 3 Pro Preview",
    displayName: "Google Gemini 3 Pro Preview (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
    categories: ["Think", "Quality", "Actions", "Multimodal"],
  },
  {
    //a
    name: "Gemini 2.5 Pro",
    displayName: "Google Gemini 2.5 Pro",
    url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
    categories: ["Think", "Actions", "Fast", "Reliable"],
  },
  {
    //b
    name: "Gemini 2.5 Flash",
    displayName: "Google Gemini 2.5 Flash",
    url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
    categories: ["Fast", "Actions", "Affordable"],
  },
  {
    //b
    name: "Gemini 2.0 Flash",
    displayName: "Google Gemini 2.0 Flash",
    url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
    categories: ["Fast", "Consistent", "Generalist"],
  },
  {
    //b
    name: "Gemini 2.0 Flash Lite",
    displayName: "Google Gemini 2.0 Flash Lite",
    url: "https://dlvkyia8i4zmz.cloudfront.net/WvbnOppxT6CIuxFkB7Wn_Frame__4_.svg",
    categories: ["Fast", "Affordable", "Lightweight"],
  },
];

const grokModels = [
  {
    // a
    name: "grok-4",
    displayName: "X Grok 4 (Recommended)",
    url: "https://dlvkyia8i4zmz.cloudfront.net/5p8NzQLS3KdleTBwHJBg_svgviewer_output__3_.svg",
    categories: ["Think", "Actions", "Actions", "Context"],
  },
  {
    //a
    name: "grok-3",
    displayName: "X Grok 3",
    url: "https://dlvkyia8i4zmz.cloudfront.net/5p8NzQLS3KdleTBwHJBg_svgviewer_output__3_.svg",
    categories: ["Actions", "Actions", "Balanced", "Think"],
  },
  {
    //a
    name: "grok-3-fast",
    displayName: "X Grok 3 Fast",
    url: "https://dlvkyia8i4zmz.cloudfront.net/5p8NzQLS3KdleTBwHJBg_svgviewer_output__3_.svg",
    categories: ["Fast", "Actions", "Actions", "Lightweight"],
  },
  {
    //a
    name: "grok-3-mini",
    displayName: "X Grok 3 Mini",
    url: "https://dlvkyia8i4zmz.cloudfront.net/5p8NzQLS3KdleTBwHJBg_svgviewer_output__3_.svg",
    categories: ["Fast", "Affordable", "Actions", "Lightweight"],
  },
  {
    //a
    name: "grok-3-mini-fast",
    displayName: "X Grok 3 Mini Fast",
    url: "https://dlvkyia8i4zmz.cloudfront.net/5p8NzQLS3KdleTBwHJBg_svgviewer_output__3_.svg",
    categories: ["UltraFast", "Lightweight", "Actions", "Basic"],
  },
];

const deepSeekModels = [
  {
    name: "deepseek-chat",
    displayName: "DeepSeek Chat V3",
    url: "https://dlvkyia8i4zmz.cloudfront.net/14mmMzyORaS0FZ38yD9f_Frame__11_.svg",
    categories: ["Technical", "Actions"],
  },
];

const groqModels = [
  //preview models
  {
    name: "kimi-k2",
    displayName: "Moonshot Kimi K2",
    url: "https://dlvkyia8i4zmz.cloudfront.net/stLo7VY4RrGJ9dkDZLPh_svgviewer_output__4_.svg",
    categories: ["Think", "Coding", "Actions", "Context"],
  },
  {
    name: "llama-4-maverick-17b-128e-instruct",
    displayName: "Meta LLaMA 4 Maverick",
    url: "https://dlvkyia8i4zmz.cloudfront.net/stLo7VY4RrGJ9dkDZLPh_svgviewer_output__4_.svg",
    categories: ["Think", "Quality", "Context"],
  },

  // --- Production Models ---
  {
    name: "openai/gpt-oss-120b",
    displayName: "OpenAI GPT-OSS 120B",
    url: "https://dlvkyia8i4zmz.cloudfront.net/stLo7VY4RrGJ9dkDZLPh_svgviewer_output__4_.svg",
    categories: ["OpenSource", "Think", "Actions", "Efficient"],
  },
];

const thinkingModels = [
  "gpt-5",
  // "gpt-5.1-thinking",
  "gpt-5-mini",
  // "gpt-5-nano",
  // "4-sonnet",
  // "3.7-sonnet",
  "Gemini 3 Pro Preview",
  "Gemini 2.5 Pro",
  "Gemini 2.5 Flash",
  // "grok-4",
  // "grok-3",
  // "grok-3-fast",
  "openai/gpt-oss-120b",
];

export const categoryIcons: Record<string, React.FC> = {
  Fast: FastIcon,
  Affordable: AffordableSvg,
  Balanced: BalancedSvg,
  Basic: BasicSvg,
  Coding: CodingSvg,
  Complex: BasicSvg,
  Consistent: ConsistentSvg,
  Context: ContextSvg,
  Generalist: GeneralistSvg,
  Lightweight: LightweightSvg,
  Multimodal: MutltimodalSvg,
  Quality: QualitySvg,
  Think: ReasonSvg,
  Reliable: ReliableSvg,
  Technical: TechnicalSvg,
  Actions: ToolsSvg,
  UltraFast: UltraFastSvg,
  OpenSource: OpenSourceSvg,
};

const models = [
  ...claudeModels,
  ...openAiModels,
  ...geminiModels,
  // ...grokModels,
  ...groqModels,
  ...deepSeekModels,
];

const flags = {
  Pashto: "AF",
  German: "DE",
  Spanish: "ES",
  French: "FR",
  Italian: "IT",
  Portuguese: "PT",
  Polish: "PL",
  Ukrainian: "UA",
  Somali: "SO",
  English: "GB",
  "English (US)": "US",
  "English (UK)": "GB",
  Afrikaans: "ZA",
  Azerbaijani: "AZ",
  Indonesian: "ID",
  "Malaysian Malay": "MY",
  Malay: "BN",
  Javanese: "ID",
  Sundanese: "ID",
  Bosnian: "BA",
  Catalan: "AD",
  Czech: "CZ",
  Chichewa: "MW",
  Welsh: "GB",
  Danish: "DK",
  Estonian: "EE",
  Esperanto: "BR",
  Basque: "ES",
  Irish: "IE",
  Galician: "ES",
  Croatian: "HR",
  Xhosa: "ZA",
  Zulu: "ZA",
  Icelandic: "IS",
  Swahili: "TZ",
  "Haitian Creole": "HT",
  Kurdish: "TR",
  Latin: "VA",
  Latvian: "LV",
  Luxembourgish: "LU",
  Lithuanian: "LT",
  Hungarian: "HU",
  Malagasy: "MG",
  Maltese: "MT",
  Maori: "NZ",
  Dutch: "NL",
  Norwegian: "NO",
  Uzbek: "UZ",
  Romanian: "RO",
  Sesotho: "ZA",
  Albanian: "AL",
  Slovak: "SK",
  Slovenian: "SI",
  Finnish: "FI",
  Swedish: "SE",
  Tagalog: "PH",
  Tatar: "RU",
  Turkish: "TR",
  Vietnamese: "VN",
  Yoruba: "NG",
  Greek: "GR",
  Belarusian: "BY",
  Bulgarian: "BG",
  Kyrgyz: "KG",
  Kazakh: "KZ",
  Macedonian: "MK",
  Mongolian: "MN",
  Russian: "RU",
  Serbian: "RS",
  Tajik: "TJ",
  Georgian: "GE",
  Armenian: "AM",
  Yiddish: "IL",
  Hebrew: "IL",
  Uyghur: "CN",
  Urdu: "PK",
  Arabic: "EG",
  Persian: "IR",
  Nepali: "NP",
  Marathi: "IN",
  Hindi: "IN",
  Bengali: "IN",
  Punjabi: "IN",
  Gujarati: "IN",
  Oriya: "IN",
  Tamil: "IN",
  Telugu: "IN",
  Kannada: "IN",
  Malayalam: "IN",
  Sinhala: "LK",
  Thai: "TH",
  Lao: "LA",
  Burmese: "MM",
  Khmer: "KH",
  Korean: "KR",
  Chinese: "CN",
  "Traditional Chinese": "CN",
  Japanese: "JP",
};
const usageLimit = [25, 50, 100, 200];
const replyLength = [
  {
    id: "auto",
    title: "Auto",
    description: "Let AI decide the right length based on the context",
  },
  {
    id: "short",
    title: "Short & Concise",
    description: "1-2 sentences (20-50 words)",
  },
  {
    id: "medium",
    title: "Standard",
    description: "2-4 sentences (50-120 words)",
  },
  {
    id: "long",
    title: "Detailed",
    description: "4-10 sentences (120-350 words)",
  },
];

const suggestions = [
  { label: "Email", question: "Please provide your email address?" },
  { label: "Phone", question: "Please provide your phone number?" },
  { label: "Name", question: "Please provide your full name?" },
  { label: "Company", question: "Please provide your company name?" },
  { label: "Job", question: "Please provide your job title?" },
  { label: "Address", question: "Please provide your current address?" },
];

const lightColors = [
  "#706AFF",
  "#DFA836",
  "#5CCF3D",
  "#E75A5A",
  "#4DA0E3",
  "#446CB1",
];
const darkColors = [
  "#A7A4FF",
  "#FFDC94",
  "#92D381",
  "#ED8888",
  "#83BAE7",
  "#6685B9",
];

const themeColors = [
  { id: 1, light: "#A7A4FF", dark: "#F64F9E" },
  { id: 2, light: "#FFDC94", dark: "#706AFF" },
  { id: 3, light: "#92D381", dark: "#FF5257" },
  { id: 4, light: "#ED8888", dark: "#F6821B" },
  { id: 5, light: "#83BAE7", dark: "#FCB826" },
];

const gradientColors = [
  {
    id: 1,
    gradient: "linear-gradient(180deg, #FF930F 0%, #FFF95B 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
  {
    id: 2,
    gradient: "linear-gradient(0deg, #FFF 19.44%, #FB5F66 99.95%)",
    btnBgColor: "linear-gradient(90deg, #FF787E 0%, #E3232C 100%)",
    btnTextColor: "#fff",
  },
  {
    id: 3,
    gradient: "linear-gradient(180deg, #FF0F7B 0%, #FFD3E7 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
  {
    id: 4,
    gradient: "linear-gradient(180deg, #0061FF 0%, #E5EFFF 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
  {
    id: 5,
    gradient: "linear-gradient(180deg, #9D1760 0%, #FFD7ED 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
  {
    id: 6,
    gradient: "linear-gradient(180deg, #FF31D2 0%, #FFF 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
  {
    id: 7,
    gradient: "linear-gradient(180deg, #C196FF 0%, #9868DD 100%)",
    btnBgColor: "#fff",
    btnTextColor: "#444",
  },
  {
    id: 8,
    gradient: "linear-gradient(180deg, #020021 0%, #808080 100%)",
    btnBgColor: "#fff",
    btnTextColor: "#444",
  },
  {
    id: 9,
    gradient: "linear-gradient(180deg, #083D77 0%, #3671B1 100%)",
    btnBgColor: "#fff",
    btnTextColor: "#444",
  },
  {
    id: 10,
    gradient: "linear-gradient(180deg, #237131 0%, #69F081 100%)",
    btnBgColor: "#222",
    btnTextColor: "#fff",
  },
];

// export const fetchFontTypes = async () => {
//   try {
//     const response: any = await axiosGet("/users/getFonts");

//     if (!response.data) {
//       throw new Error(`HTTP error! status: ${response.status}`);
//     }

//     const fontsData = await response?.data?.data;
//     const mappedFonts = fontsData
//       .filter((font) => font.isActive)
//       .map((font) => ({
//         fontURL: font.url,
//         fontFamily: font.name,
//         displayName: font.name,
//       }))
//       .sort((a, b) => a.displayName.localeCompare(b.displayName));

//     return mappedFonts;
//   } catch (error) {
//     console.error("Error fetching fonts:", error);

//     return [
//       {
//         fontURL:
//           "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap",
//         fontFamily: "Inter",
//         displayName: "Inter",
//       },
//       {
//         fontURL:
//           "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap",
//         fontFamily: "Roboto",
//         displayName: "Roboto",
//       },
//     ];
//   }
// };

export const loadFontCSS = async (fontConfig) => {
  const { fontURL, fontFamily, displayName } = fontConfig;

  try {
    const linkId = `font-link-${fontFamily.replace(/\s+/g, "-").toLowerCase()}`;
    let linkElement: any = document.getElementById(linkId);

    if (!linkElement) {
      linkElement = document.createElement("link");
      linkElement.id = linkId;
      linkElement.rel = "stylesheet";
      linkElement.href = fontURL;
      document.head.appendChild(linkElement);

      return new Promise((resolve, reject) => {
        linkElement.onload = () => {
          resolve(true);
        };
        linkElement.onerror = () => {
          console.error(`❌ Font failed: ${displayName}`);
          reject(false);
        };
      });
    }
    return true;
  } catch (error) {
    console.error(`❌ Font loading error for ${displayName}:`, error);
    return false;
  }
};

// Enhanced useFontLoader hook with preloading capabilities
export const useFontLoader = () => {
  const [loadedFonts, setLoadedFonts] = useState(new Set());
  const [loadingFonts, setLoadingFonts] = useState(new Set());
  const [failedFonts, setFailedFonts] = useState(new Set());

  const loadFont = React.useCallback(
    async (fontConfig, retries = 1) => {
      if (loadedFonts.has(fontConfig.fontFamily)) {
        return true;
      }

      if (loadingFonts.has(fontConfig.fontFamily)) {
        return false;
      }

      if (failedFonts.has(fontConfig.fontFamily) && retries === 0) {
        return false;
      }

      setLoadingFonts((prev) => new Set([...prev, fontConfig.fontFamily]));

      try {
        const success = await loadFontCSS(fontConfig);

        setLoadingFonts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fontConfig.fontFamily);
          return newSet;
        });

        if (success) {
          setLoadedFonts((prev) => new Set([...prev, fontConfig.fontFamily]));
          setFailedFonts((prev) => {
            const newSet = new Set(prev);
            newSet.delete(fontConfig.fontFamily);
            return newSet;
          });
          return true;
        } else {
          if (retries > 0) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return await loadFont(fontConfig, retries - 1);
          } else {
            setFailedFonts((prev) => new Set([...prev, fontConfig.fontFamily]));
            return false;
          }
        }
      } catch (error) {
        console.error(
          `🚨 Font loading error for ${fontConfig.displayName}:`,
          error,
        );

        setLoadingFonts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fontConfig.fontFamily);
          return newSet;
        });

        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return await loadFont(fontConfig, retries - 1);
        } else {
          setFailedFonts((prev) => new Set([...prev, fontConfig.fontFamily]));
          return false;
        }
      }
    },
    [loadedFonts, loadingFonts, failedFonts],
  );

  // New function to preload multiple fonts
  const preloadFonts = React.useCallback(
    async (fontConfigs: any, maxConcurrent: any = 3) => {
      const chunks: any = [];
      for (let i = 0; i < fontConfigs.length; i += maxConcurrent) {
        chunks.push(fontConfigs.slice(i, i + maxConcurrent));
      }

      for (const chunk of chunks) {
        await Promise.allSettled(
          chunk.map((fontConfig) => loadFont(fontConfig)),
        );
        // Small delay between chunks to prevent overwhelming the browser
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    },
    [loadFont],
  );

  return { loadFont, preloadFonts, loadedFonts, loadingFonts, failedFonts };
};

// Updated useFontTypes function with infinite loading support
export const useFontTypes = (search = "") => {
  const [fontsPreloaded, setFontsPreloaded] = useState(false);
  const { preloadFonts } = useFontLoader();
  const {
    data: fontTypes = [],
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetFonts(search);

  useEffect(() => {
    // Preload first batch of fonts immediately for better UX when data is available
    if (fontTypes.length > 0 && !fontsPreloaded) {
      const initialFonts = fontTypes.slice(0, Math.min(10, fontTypes.length));
      preloadFonts(initialFonts);
      setFontsPreloaded(true);
    }
  }, [fontTypes, fontsPreloaded, preloadFonts]);

  const preloadAllFonts = React.useCallback(async () => {
    if (fontTypes.length > 0) {
      // Preload fonts in batches when dropdown is opened
      const remainingFonts = fontTypes.slice(10);
      await preloadFonts(remainingFonts);
    }
  }, [fontTypes, preloadFonts]);

  const refetchFonts = React.useCallback(() => {
    return refetch();
  }, [refetch]);

  // Function to load more fonts when user scrolls
  const loadMoreFonts = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    fontTypes,
    isLoading,
    error: error?.message || null,
    refetchFonts,
    preloadAllFonts,
    fontsPreloaded,
    // New properties for infinite loading
    loadMoreFonts,
    hasNextPage,
    isFetchingNextPage,
  };
};

// let scriptPath =
//   "https://og-builder-cdn.outgrow.co/assets/fonts/templateFonts/fonts_varient";
// export const fontTypes = [
//   {
//     fontURL: `https://fonts.chatbotbuilder.net/1754037901922-U_lrjyFIwO1N-Stereofidelic.otf`,
//     fontFamily: `Stereofidelic`,
//     displayName: `Stereofidelic`,
//   },
//   {
//     fontURL: `${scriptPath}/Dosis.css`,
//     fontFamily: `Dosis`,
//     displayName: `Dosis`,
//   },
//   {
//     fontURL: `${scriptPath}/JosefinSans.css`,
//     fontFamily: `Josefin Sans`,
//     displayName: `Josefin Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/AlegreyaSans.css`,
//     fontFamily: `Alegreya Sans`,
//     displayName: `Alegreya Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/Cicle_Gordita.css`,
//     fontFamily: `Cicle`,
//     displayName: `Cicle`,
//   },
//   {
//     fontURL: `${scriptPath}/Amaranth.css`,
//     fontFamily: `Amaranth`,
//     displayName: `Amaranth`,
//   },
//   {
//     fontURL: `${scriptPath}/clear_sans.css`,
//     fontFamily: `Clear Sans`,
//     displayName: `Clear Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/CooperHewitt.css`,
//     fontFamily: `Cooper Hewitt`,
//     displayName: `Cooper Hewitt`,
//   },
//   {
//     fontURL: `${scriptPath}/Courier_Prime_Sans.css`,
//     fontFamily: `Courier Prime Sans`,
//     displayName: `Courier Prime Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/lato.css`,
//     fontFamily: `Lato`,
//     displayName: `Lato`,
//   },
//   {
//     fontURL: `${scriptPath}/Trajan_Pro.css`,
//     fontFamily: `Trajan Pro`,
//     displayName: `Trajan Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/Orkney.css`,
//     fontFamily: `Orkney`,
//     displayName: `Orkney`,
//   },
//   {
//     fontURL: `${scriptPath}/PT_serif.css`,
//     fontFamily: `PT_Serif`,
//     displayName: `PT Serif`,
//   },
//   {
//     fontURL: `${scriptPath}/open_sans.css`,
//     fontFamily: `OpenSans`,
//     displayName: `OpenSans`,
//   },
//   {
//     fontURL: `${scriptPath}/afarat_ibn.css`,
//     fontFamily: `afarat_ibn_bladyregular`,
//     displayName: `Afarat Ibn Blady Regular`,
//   },
//   {
//     fontURL: `${scriptPath}/poppins.css`,
//     fontFamily: `Poppins`,
//     displayName: `Poppins`,
//   },
//   {
//     fontURL: `${scriptPath}/work_sans.css`,
//     fontFamily: `work_sans`,
//     displayName: `Work Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/droidserif.css`,
//     fontFamily: `droid_serif`,
//     displayName: `Droid Serif`,
//   },
//   {
//     fontURL: `${scriptPath}/Nexa.css`,
//     fontFamily: `Nexa`,
//     displayName: `Nexa`,
//   },
//   {
//     fontURL: `${scriptPath}/Karla.css`,
//     fontFamily: `Karla`,
//     displayName: `Karla`,
//   },
//   {
//     fontURL: `${scriptPath}/Lobster.css`,
//     fontFamily: `lobster`,
//     displayName: `Lobster`,
//   },
//   {
//     fontURL: `${scriptPath}/Roboto_Regular.css`,
//     fontFamily: `roboto`,
//     displayName: `Roboto Regular`,
//   },
//   {
//     fontURL: `${scriptPath}/alef.css`,
//     fontFamily: `alef`,
//     displayName: `Alef`,
//   },
//   {
//     fontURL: `${scriptPath}/comfortaa.css`,
//     fontFamily: `comfortaa`,
//     displayName: `Comfortaa`,
//   },
//   {
//     fontURL: `${scriptPath}/PT-Sans.css`,
//     fontFamily: `PT-Sans`,
//     displayName: `PT-Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/PT-mono.css`,
//     fontFamily: `PT_mono`,
//     displayName: `PT-Mono`,
//   },
//   {
//     fontURL: `${scriptPath}/ubuntu.css`,
//     fontFamily: `Ubuntu`,
//     displayName: `Ubuntu`,
//   },
//   {
//     fontURL: `${scriptPath}/Helvetica.css`,
//     fontFamily: `Helvetica`,
//     displayName: `Helvetica`,
//   },
//   {
//     fontURL: `${scriptPath}/Aller.css`,
//     fontFamily: `Aller`,
//     displayName: `Aller`,
//   },
//   {
//     fontURL: `${scriptPath}/SourceSansPro.css`,
//     fontFamily: `sourcesanspro`,
//     displayName: `SourceSansPro`,
//   },
//   {
//     fontURL: `${scriptPath}/axiforma.css`,
//     fontFamily: `axiforma`,
//     displayName: `Axiforma`,
//   },
//   {
//     fontURL: `${scriptPath}/hk_grotesk.css`,
//     fontFamily: `hkgrotesk`,
//     displayName: `HK Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/Maven_Pro.css`,
//     fontFamily: `mavenpro`,
//     displayName: `Maven Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/Baskerville.css`,
//     fontFamily: `Baskerville`,
//     displayName: `Baskerville`,
//   },
//   {
//     fontURL: `${scriptPath}/Roboto_Slab.css`,
//     fontFamily: `roboto_slab`,
//     displayName: `Roboto Slab`,
//   },
//   {
//     fontURL: `${scriptPath}/sharpsans.css`,
//     fontFamily: `sharpsans`,
//     displayName: `Sharp Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/aktivgrotesk.css`,
//     fontFamily: `aktivgrotesk`,
//     displayName: `Aktiv Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/proximanova.css`,
//     fontFamily: `proximanova`,
//     displayName: `Proxima Nova`,
//   },
//   {
//     fontURL: `${scriptPath}/Jenna_sue.css`,
//     fontFamily: `Jenna_sue`,
//     displayName: `Jenna Sue`,
//   },
//   {
//     fontURL: `${scriptPath}/SuisseWorks.css`,
//     fontFamily: `suisseworks`,
//     displayName: `Suisse Works`,
//   },
//   {
//     fontURL: `${scriptPath}/neosans.css`,
//     fontFamily: `neosans`,
//     displayName: `Neo Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/architects-daughter.css`,
//     fontFamily: `architectsdaughter`,
//     displayName: `Architects Daughter`,
//   },
//   {
//     fontURL: `${scriptPath}/freeland.css`,
//     fontFamily: `freeland`,
//     displayName: `Freeland`,
//   },
//   {
//     fontURL: `${scriptPath}/akzidenzgrotesk.css`,
//     fontFamily: `akzidenzgroteskbq`,
//     displayName: `Akzidenz Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/louize.css`,
//     fontFamily: `louize`,
//     displayName: `Louize`,
//   },
//   { fontURL: `${scriptPath}/ipa.css`, fontFamily: `ipam`, displayName: `IPA` },
//   {
//     fontURL: `${scriptPath}/apexrounded.css`,
//     fontFamily: `apexrounded`,
//     displayName: `Apex Rounded Book`,
//   },
//   {
//     fontURL: `${scriptPath}/apercu.css`,
//     fontFamily: `apercu`,
//     displayName: `Apercu`,
//   },
//   {
//     fontURL: `${scriptPath}/heebo.css`,
//     fontFamily: `heebo`,
//     displayName: `Heebo`,
//   },
//   {
//     fontURL: `${scriptPath}/muli.css`,
//     fontFamily: `Muli`,
//     displayName: `Muli`,
//   },
//   {
//     fontURL: `${scriptPath}/sarabun.css`,
//     fontFamily: `sarabun`,
//     displayName: `Sarabun`,
//   },
//   {
//     fontURL: `${scriptPath}/prompt.css`,
//     fontFamily: `prompt`,
//     displayName: `Prompt`,
//   },
//   {
//     fontURL: `${scriptPath}/nunito.css`,
//     fontFamily: `nunito`,
//     displayName: `Nunito`,
//   },
//   {
//     fontURL: `${scriptPath}/Predige.css`,
//     fontFamily: `Predige`,
//     displayName: `Predige`,
//   },
//   {
//     fontURL: `${scriptPath}/Circular.css`,
//     fontFamily: `CircularStd`,
//     displayName: `CircularStd`,
//   },
//   {
//     fontURL: `${scriptPath}/Notosanstc.css`,
//     fontFamily: `Noto Sans TC`,
//     displayName: `Noto Sans Tc`,
//   },
//   {
//     fontURL: `${scriptPath}/northwoods.css`,
//     fontFamily: `Northwoods`,
//     displayName: `Northwoods`,
//   },
//   {
//     fontURL: `${scriptPath}/Calibri.css`,
//     fontFamily: `Calibri`,
//     displayName: `Calibri`,
//   },
//   {
//     fontURL: `${scriptPath}/Guild.css`,
//     fontFamily: `GuildCircular`,
//     displayName: `GuildCircular`,
//   },
//   {
//     fontURL: `${scriptPath}/Gilroy.css`,
//     fontFamily: `Gilroy`,
//     displayName: `Gilroy`,
//   },
//   {
//     fontURL: `${scriptPath}/genesiscaresans.css`,
//     fontFamily: `genesiscaresans`,
//     displayName: `Genesis Care Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/fira_Sans.css`,
//     fontFamily: `firasans`,
//     displayName: `Fira Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/EffraPro.css`,
//     fontFamily: `effrapro`,
//     displayName: `EffraPro`,
//   },
//   {
//     fontURL: `${scriptPath}/AldaPro.css`,
//     fontFamily: `AldaPro`,
//     displayName: `AldaPro`,
//   },
//   {
//     fontURL: `${scriptPath}/glober.css`,
//     fontFamily: `glober`,
//     displayName: `Glober`,
//   },
//   {
//     fontURL: `${scriptPath}/nassim.css`,
//     fontFamily: `NassimArabic`,
//     displayName: `Nassim Arabic`,
//   },
//   {
//     fontURL: `${scriptPath}/Graphik.css`,
//     fontFamily: `Graphik`,
//     displayName: `Graphik`,
//   },
//   {
//     fontURL: `${scriptPath}/MyriadPro.css`,
//     fontFamily: `MyriadWebPro`,
//     displayName: `MyriadWebPro`,
//   },
//   {
//     fontURL: `${scriptPath}/Vazir.css`,
//     fontFamily: `vazir`,
//     displayName: `Vazir`,
//   },
//   {
//     fontURL: `${scriptPath}/BrownPro.css`,
//     fontFamily: `BrownPro`,
//     displayName: `BrownPro`,
//   },
//   {
//     fontURL: `${scriptPath}/ibmPlex.css`,
//     fontFamily: `ibmplex`,
//     displayName: `IBM Plex`,
//   },
//   {
//     fontURL: `${scriptPath}/fselliotpro.css`,
//     fontFamily: `fselliotpro`,
//     displayName: `Fselliot Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/lusitana.css`,
//     fontFamily: `lusitana`,
//     displayName: `Lusitana`,
//   },
//   {
//     fontURL: `${scriptPath}/crimson.css`,
//     fontFamily: `crimson_text`,
//     displayName: `Crimson`,
//   },
//   {
//     fontURL: `${scriptPath}/Gotham.css`,
//     fontFamily: `Gotham`,
//     displayName: `Gotham`,
//   },
//   {
//     fontURL: `${scriptPath}/azoSans.css`,
//     fontFamily: `ruiabreu_azosans`,
//     displayName: `Azo Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/assistant.css`,
//     fontFamily: `assistant`,
//     displayName: `Assistant`,
//   },
//   {
//     fontURL: `${scriptPath}/Grotesque.css`,
//     fontFamily: `Grotesque`,
//     displayName: `Basis Grotesque`,
//   },
//   {
//     fontURL: `${scriptPath}/AkkuratPro.css`,
//     fontFamily: `AkkuratPro`,
//     displayName: `AkkuratPro`,
//   },
//   {
//     fontURL: `${scriptPath}/AdobeClean.css`,
//     fontFamily: `AdobeClean`,
//     displayName: `AdobeClean`,
//   },
//   {
//     fontURL: `${scriptPath}/beausanspro.css`,
//     fontFamily: `beausanspro`,
//     displayName: `BeauSansPro`,
//   },
//   {
//     fontURL: `${scriptPath}/pk_futura_neo.css`,
//     fontFamily: `Conv_PFFuturaNeu`,
//     displayName: `PF Futura Neo`,
//   },
//   {
//     fontURL: `${scriptPath}/Amadeus.css`,
//     fontFamily: `amadeus`,
//     displayName: `Amadeus`,
//   },
//   {
//     fontURL: `${scriptPath}/DINnextLtPro.css`,
//     fontFamily: `DINNextLTPro`,
//     displayName: `DINNextLTPro`,
//   },
//   {
//     fontURL: `${scriptPath}/bank-gothic.css`,
//     fontFamily: `bank_gothic`,
//     displayName: `Bank Gothic`,
//   },
//   {
//     fontURL: `${scriptPath}/Oswald.css`,
//     fontFamily: `oswald`,
//     displayName: `Oswald`,
//   },
//   {
//     fontURL: `${scriptPath}/Playfairdisplay.css`,
//     fontFamily: `playfairdisplay`,
//     displayName: `PlayFair Display`,
//   },
//   {
//     fontURL: `${scriptPath}/futura.css`,
//     fontFamily: `futura`,
//     displayName: `Futura`,
//   },
//   {
//     fontURL: `${scriptPath}/fs_albert.css`,
//     fontFamily: `fs_albert`,
//     displayName: `FS Albert`,
//   },
//   {
//     fontURL: `${scriptPath}/Franklin-Gothic-Demi.css`,
//     fontFamily: `Franklin-Gothic-Demi`,
//     displayName: `Franklin Gothic Demi`,
//   },
//   {
//     fontURL: `${scriptPath}/Pluto.css`,
//     fontFamily: `pluto`,
//     displayName: `Pluto`,
//   },
//   {
//     fontURL: `${scriptPath}/dmsans.css`,
//     fontFamily: `dmsans`,
//     displayName: `DM Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/Gravure.css`,
//     fontFamily: `Gravure`,
//     displayName: `Gravure`,
//   },
//   {
//     fontURL: `${scriptPath}/Campton.css`,
//     fontFamily: `campton`,
//     displayName: `Campton`,
//   },
//   {
//     fontURL: `${scriptPath}/Merriweather.css`,
//     fontFamily: `merriweather`,
//     displayName: `Merriweather`,
//   },
//   {
//     fontURL: `${scriptPath}/TradeGothic.css`,
//     fontFamily: `TradeGothicNextLTPro`,
//     displayName: `Trade Gothic`,
//   },
//   {
//     fontURL: `${scriptPath}/botanikastd.css`,
//     fontFamily: `botanikastd`,
//     displayName: `Botanika Std`,
//   },
//   {
//     fontURL: `${scriptPath}/AvenirNext.css`,
//     fontFamily: `AvenirNext`,
//     displayName: `AvenirNext`,
//   },
//   {
//     fontURL: `${scriptPath}/nanumgothiccoding.css`,
//     fontFamily: `nanumgothiccoding`,
//     displayName: `Nanumgothiccoding`,
//   },
//   {
//     fontURL: `${scriptPath}/DuplicateIonic.css`,
//     fontFamily: `DuplicateIonic`,
//     displayName: `Duplicate Ionic`,
//   },
//   {
//     fontURL: `${scriptPath}/AgletSans.css`,
//     fontFamily: `PYEFMS AgletSans`,
//     displayName: `AgletSans`,
//   },
//   {
//     fontURL: `${scriptPath}/44555.css`,
//     fontFamily: `Conv_44555`,
//     displayName: `Conv_44555`,
//   },
//   {
//     fontURL: `${scriptPath}/Narrow.css`,
//     fontFamily: `NarrowTypeface`,
//     displayName: `Narrow`,
//   },
//   {
//     fontURL: `${scriptPath}/Artifakt.css`,
//     fontFamily: `artifakt`,
//     displayName: `Artifakt`,
//   },
//   {
//     fontURL: `${scriptPath}/barlow.css`,
//     fontFamily: `barlowcondensed`,
//     displayName: `Barlow Condensed`,
//   },
//   {
//     fontURL: `${scriptPath}/wyoming.css`,
//     fontFamily: `wyoming`,
//     displayName: `Wyoming`,
//   },
//   {
//     fontURL: `${scriptPath}/Giovanni.css`,
//     fontFamily: `Giovanni`,
//     displayName: `Giovanni`,
//   },
//   {
//     fontURL: `${scriptPath}/flexo.css`,
//     fontFamily: `flexo`,
//     displayName: `Flexo`,
//   },
//   {
//     fontURL: `${scriptPath}/LemonMilk.css`,
//     fontFamily: `lemonmilk`,
//     displayName: `Lemon Milk`,
//   },
//   {
//     fontURL: `${scriptPath}/Arial.css`,
//     fontFamily: `arial`,
//     displayName: `Arial`,
//   },
//   {
//     fontURL: `${scriptPath}/CeraPro.css`,
//     fontFamily: `cerapro`,
//     displayName: `Cera Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/sangbleusunriseLight.css`,
//     fontFamily: `sangbleusunrise`,
//     displayName: `Sangbleu Sunrise Light`,
//   },
//   {
//     fontURL: `${scriptPath}/Klarna.css`,
//     fontFamily: `Klarna`,
//     displayName: `Klarna`,
//   },
//   {
//     fontURL: `${scriptPath}/BreeSerif.css`,
//     fontFamily: `breeserif`,
//     displayName: `BreeSerif`,
//   },
//   {
//     fontURL: `${scriptPath}/Patuaone.css`,
//     fontFamily: `patuaone`,
//     displayName: `Patua One`,
//   },
//   {
//     fontURL: `${scriptPath}/DaxCompact.css`,
//     fontFamily: `DaxCompact`,
//     displayName: `Dax Compact`,
//   },
//   {
//     fontURL: `${scriptPath}/vollkorn.css`,
//     fontFamily: `vollkorn`,
//     displayName: `Vollkorn`,
//   },
//   {
//     fontURL: `${scriptPath}/suisseintl.css`,
//     fontFamily: `suisseintl`,
//     displayName: `Suisse Intl`,
//   },
//   {
//     fontURL: `${scriptPath}/hurmegeometricsans.css`,
//     fontFamily: `hurmegeometricsans`,
//     displayName: `Hurme Geometric Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/Brandon.css`,
//     fontFamily: `brandon`,
//     displayName: `Brandon`,
//   },
//   {
//     fontURL: `${scriptPath}/monumentgrotesk.css`,
//     fontFamily: `monumentgrotesk`,
//     displayName: `Monument Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/inter.css`,
//     fontFamily: `Inter`,
//     displayName: `Inter`,
//   },
//   {
//     fontURL: `${scriptPath}/LeMondeJournal.css`,
//     fontFamily: `LeMondeJournalStd`,
//     displayName: `Le Monde Journal`,
//   },
//   {
//     fontURL: `${scriptPath}/Cairo.css`,
//     fontFamily: `cairo`,
//     displayName: `Cairo`,
//   },
//   {
//     fontURL: `${scriptPath}/Lateef.css`,
//     fontFamily: `lateef`,
//     displayName: `Lateef`,
//   },
//   {
//     fontURL: `${scriptPath}/Mirza.css`,
//     fontFamily: `mirza`,
//     displayName: `Mirza`,
//   },
//   {
//     fontURL: `${scriptPath}/ArtegraSans.css`,
//     fontFamily: `Artegra_Sans`,
//     displayName: `ArtegraSans`,
//   },
//   {
//     fontURL: `${scriptPath}/TimeNewRoman.css`,
//     fontFamily: `times_new_roman`,
//     displayName: `Times New Roman`,
//   },
//   {
//     fontURL: `${scriptPath}/Articulat.css`,
//     fontFamily: `articulat`,
//     displayName: `Articulat`,
//   },
//   {
//     fontURL: `${scriptPath}/National.css`,
//     fontFamily: `National2Web`,
//     displayName: `National`,
//   },
//   {
//     fontURL: `${scriptPath}/Jost.css`,
//     fontFamily: `Conv_Jost`,
//     displayName: `Jost`,
//   },
//   {
//     fontURL: `${scriptPath}/ReplicaPro.css`,
//     fontFamily: `ReplicaPro`,
//     displayName: `Replica Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/TTNormsPro.css`,
//     fontFamily: `TTNormsPro`,
//     displayName: `TT Norms Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/InterlivingScript.css`,
//     fontFamily: `InterlivingScript`,
//     displayName: `Interliving Script`,
//   },
//   {
//     fontURL: `${scriptPath}/GT-America.css`,
//     fontFamily: `gt-america`,
//     displayName: `GT America`,
//   },
//   {
//     fontURL: `${scriptPath}/trebuchet.css`,
//     fontFamily: `trebuchet`,
//     displayName: `Trebuchet`,
//   },
//   {
//     fontURL: `${scriptPath}/WebbEllisCup.css`,
//     fontFamily: `Conv_WebbEllisCup`,
//     displayName: `WebbEllisCup`,
//   },
//   {
//     fontURL: `${scriptPath}/averta.css`,
//     fontFamily: `averta`,
//     displayName: `Averta`,
//   },
//   {
//     fontURL: `${scriptPath}/pressstart2p.css`,
//     fontFamily: `press_start_2p`,
//     displayName: `Press Start 2P`,
//   },
//   {
//     fontURL: `${scriptPath}/coresans.css`,
//     fontFamily: `coresansc`,
//     displayName: `Core Sans`,
//   },
//   {
//     fontURL: `${scriptPath}/kanit.css`,
//     fontFamily: `kanit`,
//     displayName: `Kanit`,
//   },
//   {
//     fontURL: `${scriptPath}/lexend.css`,
//     fontFamily: `lexend`,
//     displayName: `Lexend`,
//   },
//   {
//     fontURL: `${scriptPath}/bpgarial.css`,
//     fontFamily: `bpgarial`,
//     displayName: `BPG Arial`,
//   },
//   {
//     fontURL: `${scriptPath}/ogjtd.css`,
//     fontFamily: `ogjtd`,
//     displayName: `OGJ Type Design`,
//   },
//   {
//     fontURL: `${scriptPath}/zillaslab.css`,
//     fontFamily: `zillaslab`,
//     displayName: `Zilla Slab`,
//   },
//   {
//     fontURL: `${scriptPath}/halyarddis.css`,
//     fontFamily: `halyard-display`,
//     displayName: `Halyard Display`,
//   },
//   {
//     fontURL: `${scriptPath}/para-type.css`,
//     fontFamily: `para-type`,
//     displayName: `Para Type`,
//   },
//   {
//     fontURL: `${scriptPath}/gt-walsheim.css`,
//     fontFamily: `gt-walsheim`,
//     displayName: `GT Walsheim`,
//   },
//   {
//     fontURL: `${scriptPath}/sourcehansans.css`,
//     fontFamily: `sourcehansans`,
//     displayName: `Source Hansans CN`,
//   },
//   {
//     fontURL: `${scriptPath}/museo.css`,
//     fontFamily: `museo`,
//     displayName: `Museo`,
//   },
//   {
//     fontURL: `${scriptPath}/futurapt.css`,
//     fontFamily: `futurapt`,
//     displayName: `Futura PT`,
//   },
//   {
//     fontURL: `${scriptPath}/museomoderno.css`,
//     fontFamily: `museomoderno`,
//     displayName: `Museo Moderno`,
//   },
//   {
//     fontURL: `${scriptPath}/spaceG.css`,
//     fontFamily: `spaceG`,
//     displayName: `Space Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/TTCommons.css`,
//     fontFamily: `tt_commons`,
//     displayName: `TTCommons`,
//   },
//   {
//     fontURL: `${scriptPath}/Surt.css`,
//     fontFamily: `surt`,
//     displayName: `Surt`,
//   },
//   {
//     fontURL: `${scriptPath}/LyonDisplay.css`,
//     fontFamily: `LyonDisplay`,
//     displayName: `LyonDisplay`,
//   },
//   {
//     fontURL: `${scriptPath}/varelaRound.css`,
//     fontFamily: `Varela Round`,
//     displayName: `Varela Round`,
//   },
//   {
//     fontURL: `${scriptPath}/favoritExtended.css`,
//     fontFamily: `favoritExtended`,
//     displayName: `Favorit Extended`,
//   },
//   {
//     fontURL: `${scriptPath}/sometimesTimes.css`,
//     fontFamily: `sometimesTimes`,
//     displayName: `Sometimes Times`,
//   },
//   {
//     fontURL: `${scriptPath}/porscheNext.css`,
//     fontFamily: `porscheNext`,
//     displayName: `Porsche Next`,
//   },
//   {
//     fontURL: `${scriptPath}/mercuryDisplay.css`,
//     fontFamily: `mercuryDisplay`,
//     displayName: `Mercury Display`,
//   },
//   {
//     fontURL: `${scriptPath}/aeonik.css`,
//     fontFamily: `aeonik`,
//     displayName: `Aeonik`,
//   },
//   {
//     fontURL: `${scriptPath}/frank.css`,
//     fontFamily: `frank`,
//     displayName: `Frank`,
//   },
//   {
//     fontURL: `${scriptPath}/volte.css`,
//     fontFamily: `volte`,
//     displayName: `Volte`,
//   },
//   {
//     fontURL: `${scriptPath}/prudentialM.css`,
//     fontFamily: `prudentialM`,
//     displayName: `Prudential Modern`,
//   },
//   {
//     fontURL: `${scriptPath}/sourceSerifPro.css`,
//     fontFamily: `Source Serif Pro`,
//     displayName: `Source Serif Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/peclet.css`,
//     fontFamily: `peclet`,
//     displayName: `Peclet`,
//   },
//   {
//     fontURL: `${scriptPath}/agenor.css`,
//     fontFamily: `agenor`,
//     displayName: `Agenor`,
//   },
//   {
//     fontURL: `${scriptPath}/hafferxh.css`,
//     fontFamily: `hafferxh`,
//     displayName: `Hafferxh`,
//   },
//   {
//     fontURL: `${scriptPath}/vcnudge.css`,
//     fontFamily: `vcnudge`,
//     displayName: `VCNudge`,
//   },
//   {
//     fontURL: `${scriptPath}/paracentral.css`,
//     fontFamily: `paracentral`,
//     displayName: `Para Central`,
//   },
//   {
//     fontURL: `${scriptPath}/lora.css`,
//     fontFamily: `lora`,
//     displayName: `Lora`,
//   },
//   {
//     fontURL: `${scriptPath}/underground.css`,
//     fontFamily: `underground`,
//     displayName: `P22 Underground`,
//   },
//   {
//     fontURL: `${scriptPath}/mackinac.css`,
//     fontFamily: `mackinac`,
//     displayName: `P22 Mackinac`,
//   },
//   {
//     fontURL: `${scriptPath}/obviously-narw.css`,
//     fontFamily: `obviously-narw`,
//     displayName: `Obviously Narrow`,
//   },
//   {
//     fontURL: `${scriptPath}/neuehaasunicaro.css`,
//     fontFamily: `neuehaasunicaro`,
//     displayName: `Neue Haas Unica Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/sohneBuch.css`,
//     fontFamily: `sohneBuch`,
//     displayName: `Sohne Buch`,
//   },
//   {
//     fontURL: `${scriptPath}/FlamaUltracondensed.css`,
//     fontFamily: `FlamaUltracondensed`,
//     displayName: `Flama Ultra Condensed`,
//   },
//   {
//     fontURL: `${scriptPath}/FlamaCondensed.css`,
//     fontFamily: `FlamaCondensed`,
//     displayName: `Flama Condensed`,
//   },
//   {
//     fontURL: `${scriptPath}/BasicCommercial.css`,
//     fontFamily: `BasicCommercial`,
//     displayName: `Basic Commercial`,
//   },
//   {
//     fontURL: `${scriptPath}/textanarrowalt.css`,
//     fontFamily: `textanarrowalt`,
//     displayName: `Texta Narrow`,
//   },
//   {
//     fontURL: `${scriptPath}/directorsgothic.css`,
//     fontFamily: `directorsgothic`,
//     displayName: `Directors Gothic`,
//   },
//   {
//     fontURL: `${scriptPath}/plantinMTStd.css`,
//     fontFamily: `plantinMTStd`,
//     displayName: `Plantin`,
//   },
//   {
//     fontURL: `${scriptPath}/neuehaasgrotesktext.css`,
//     fontFamily: `neuehaasgrotesktext`,
//     displayName: `Neue Haas Grotesk Pro`,
//   },
//   {
//     fontURL: `${scriptPath}/satoshi.css`,
//     fontFamily: `Satoshi`,
//     displayName: `Satoshi`,
//   },
//   {
//     fontURL: `${scriptPath}/agrandir.css`,
//     fontFamily: `Agrandir`,
//     displayName: `Agrandir`,
//   },
//   {
//     fontURL: `${scriptPath}/BerrangerHandITCStd.css`,
//     fontFamily: `Berranger Hand ITC Std`,
//     displayName: `Berranger Hand ITC Std`,
//   },
//   {
//     fontURL: `${scriptPath}/ambit.css`,
//     fontFamily: `ambit`,
//     displayName: `Ambit`,
//   },
//   {
//     fontURL: `${scriptPath}/GowlingWlgBliss.css`,
//     fontFamily: `GowlingWlgBliss`,
//     displayName: `Gowling WLG Bliss`,
//   },
//   {
//     fontURL: `${scriptPath}/secular.css`,
//     fontFamily: `Secular One`,
//     displayName: `Secular One`,
//   },
//   {
//     fontURL: `${scriptPath}/Peak.css`,
//     fontFamily: `Peak`,
//     displayName: `Peak`,
//   },
//   {
//     fontURL: `${scriptPath}/ESKlarheitGrotesk.css`,
//     fontFamily: `ES Klarheit Grotesk`,
//     displayName: `ES Klarheit Grotesk`,
//   },
//   {
//     fontURL: `${scriptPath}/generalSans.css`,
//     fontFamily: `generalSans`,
//     displayName: `General Sans`,
//   },
// ].sort((a: any, b: any) => {
//   return a.displayName < b.displayName ? -1 : 1;
// });
// export const fontWeights = ["Regular", "Medium", "Bold"];
export const fontWeights = [
  { name: "Regular", value: "400" },
  { name: "Semi-Bold", value: "500" },
  { name: "Bold", value: "700" },
];

export const fontSizes = [12, 13, 14, 15, 16, 17, 18];
export const headerFontSizes = [14, 15, 16, 17, 18, 20];
export const descFontSizes = [12, 13, 14, 15];

export {
  botTone,
  language,
  usageLimit,
  replyLength,
  suggestions,
  lightColors,
  darkColors,
  themeColors,
  flags,
  models,
  openAiModels,
  claudeModels,
  geminiModels,
  grokModels,
  gradientColors,
  deepSeekModels,
  groqModels,
  thinkingModels,
};

export const botThemes = {
  light: {
    chatBgColor: "#ffffff",
    userBubbleColor: "#e5e5e5",
    color: "#fb5f66", //color we were already having so need not change for old bots based on theme
    greetingConfig: {
      headerFooterBgColor: "#eeeeee",
      typography: {
        color: "#444",
      },
    },
    typography: {
      color: "#fff",
    },
    welcomePageBgColor: "linear-gradient(0deg, #FFF 19.44%, #FB5F66 99.95%)",
    welcomePageBtnTxtColor:
      "linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
    welcomePageBtnColor:
      "linear-gradient(90deg, rgb(255, 120, 126) 0%, rgb(227, 35, 44) 100%)",
    messageBoxBgColor: "#eee",
  },
  dark: {
    chatBgColor: "#222222",
    userBubbleColor: "#404040",
    color: "#fb5f66", //color we were already having so need not change for old bots based on theme
    greetingConfig: {
      headerFooterBgColor: "#353535",
      typography: {
        color: "#fff",
      },
    },
    typography: {
      color: "#fff",
    },
    welcomePageBgColor: "linear-gradient(0deg, #FFF 19.44%, #FB5F66 99.95%)",
    welcomePageBtnTxtColor:
      "linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
    welcomePageBtnColor:
      "linear-gradient(90deg, rgb(255, 120, 126) 0%, rgb(227, 35, 44) 100%)",
    messageBoxBgColor: "#353535",
  },
};
